import React, { useEffect, useState } from 'react';
import { Layout } from '../../components/Layout';
import { TenantCreateModal } from '../../components/TenantCreateModal';
import { api } from '../../services/api';
import { Tenant } from '../../types';

interface TenantListResponse {
  tenants: Tenant[];
  totalCount: number;
}

export function TenantList() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invitingTenantId, setInvitingTenantId] = useState<string | null>(null);

  useEffect(() => {
    async function loadTenants() {
      try {
        const data = await api.get<TenantListResponse>('/admin/tenants');
        setTenants(data.tenants);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenants');
      } finally {
        setLoading(false);
      }
    }
    loadTenants();
  }, []);

  const handleTenantCreated = (tenant: Tenant) => {
    setTenants([tenant, ...tenants]);
  };

  const handleInviteAdmin = async (tenantId: string) => {
    setInvitingTenantId(tenantId);
    setError('');
    try {
      const result = await api.post<{ invitedAt: string }>(`/admin/tenants/${tenantId}/invite-admin`, {});
      setTenants(tenants.map(t => 
        t.tenantId === tenantId 
          ? { ...t, adminInvitedAt: result.invitedAt }
          : t
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite admin');
    } finally {
      setInvitingTenantId(null);
    }
  };

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Tenants</h1>
        <button
          style={{
            padding: '10px 20px',
            backgroundColor: '#1a237e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => setShowCreateModal(true)}
        >
          + New Tenant
        </button>
      </div>

      <TenantCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleTenantCreated}
      />

      {error && (
        <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: '16px' }}>Organization</th>
                <th style={{ textAlign: 'left', padding: '16px' }}>Admin</th>
                <th style={{ textAlign: 'left', padding: '16px' }}>Limits</th>
                <th style={{ textAlign: 'left', padding: '16px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '16px' }}>Created</th>
                <th style={{ textAlign: 'center', padding: '16px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.tenantId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '16px' }}>
                    <strong>{tenant.organizationName}</strong>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>{tenant.adminName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{tenant.adminEmail}</div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>{tenant.maxAdvocates} advocates</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{tenant.maxClients} clients</div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: tenant.status === 'active' ? '#e8f5e9' : '#ffebee',
                      color: tenant.status === 'active' ? '#2e7d32' : '#c62828',
                    }}>
                      {tenant.status}
                    </span>
                  </td>
                  <td style={{ padding: '16px', color: '#666' }}>
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <InviteButton
                      tenant={tenant}
                      isInviting={invitingTenantId === tenant.tenantId}
                      onInvite={() => handleInviteAdmin(tenant.tenantId)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}

interface InviteButtonProps {
  tenant: Tenant;
  isInviting: boolean;
  onInvite: () => void;
}

function InviteButton({ tenant, isInviting, onInvite }: InviteButtonProps) {
  // Already has a user ID means they've been invited
  if (tenant.adminInvitedAt || tenant.adminUserId) {
    return (
      <span
        title={`Invited ${tenant.adminInvitedAt ? new Date(tenant.adminInvitedAt).toLocaleDateString() : ''}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 12px',
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: '4px',
          fontSize: '12px',
        }}
      >
        ✓ Invited
      </span>
    );
  }

  return (
    <button
      onClick={onInvite}
      disabled={isInviting}
      title="Send invitation email to tenant admin"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        backgroundColor: isInviting ? '#e0e0e0' : '#fff3e0',
        color: '#e65100',
        border: '1px solid #ffb74d',
        borderRadius: '4px',
        cursor: isInviting ? 'not-allowed' : 'pointer',
        fontSize: '12px',
      }}
    >
      {isInviting ? 'Sending...' : '✉ Invite'}
    </button>
  );
}
